import React, { useEffect } from 'react';
import Header from '../components/Header';
import Partners from '../components/Partners';
import Compliance from '../components/Compliance';
import Footer from '../components/Footer';

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF5]">
      <Header />

      {/* Hero */}
      <div style={{ background: '#0B1F2D', paddingBottom: 64 }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 0' }}>
          <div style={{ fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7BC2B0', fontWeight: 500, marginBottom: 12 }}>Legal</div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(36px,5vw,60px)', lineHeight: 1.02, color: '#FAFAF5', letterSpacing: '-0.02em', marginBottom: 16 }}>Privacy Policy</h1>
          <p style={{ fontSize: 15, color: 'rgba(250,250,245,0.6)', lineHeight: 1.6 }}>Last updated: May 7, 2026</p>
        </div>
      </div>

      {/* Intro */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 0' }}>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)', padding: '20px 24px', borderRadius: 12, background: '#E8F3EE', borderLeft: '3px solid #1B6F5E' }}>
          This Privacy Policy explains how ESGine® ("ESGine®", "we", "us", "our") collects, uses, stores and protects personal data and other information when you visit our website, interact with us, or use the ESGine® platform.
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 96px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>

          <Section title="1. Who we are">
            <P>ESGine® is the world's only ESG-as-Code® compliance platform. Companies upload their ESG reports and receive a clause-by-clause compliance result across seven frameworks and 90+ rules. Every outcome maps to a specific regulatory clause.</P>
            <P>For the purposes of the UK General Data Protection Regulation ("UK GDPR") and, where applicable, the EU GDPR, ESGine® acts as a data controller when we determine the purposes and means of processing personal data in connection with our website, marketing and client relationships.</P>
          </Section>

          <Section title="2. How to contact us">
            <P>If you have any questions about this Privacy Policy or how we handle personal data, you can contact us at:</P>
            <div style={{ marginTop: 16, padding: '20px 24px', borderRadius: 12, border: '1px solid #E8E5DC', background: 'white' }}>
              <div style={{ fontSize: 14, color: '#0B1F2D', lineHeight: 2 }}>
                <div><strong>Email:</strong> <a href="mailto:info@esgine.io" style={{ color: '#1B6F5E' }}>info@esgine.io</a></div>
                <div><strong>United States:</strong> 20 F Street NW, #700, Washington, DC 20001, United States</div>
                <div><strong>United Kingdom:</strong> Peter House, Oxford Street, #408, Manchester, M1 5AN, United Kingdom</div>
              </div>
            </div>
          </Section>

          <Section title="3. Scope of this Privacy Policy">
            <P>This Privacy Policy applies to:</P>
            <ul style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              <Li>Visitors to our website</Li>
              <Li>Individuals who contact us or request access</Li>
              <Li>Authorized users of the ESGine® platform acting on behalf of client organizations</Li>
              <Li>Prospective clients, partners and other professional contacts</Li>
            </ul>
            <P>Where we process data on behalf of a client (for example, ESG/Climate submission packages and related files uploaded to our platform), we act as a data processor and the client's own privacy and confidentiality terms will also apply.</P>
          </Section>

          <Section title="4. What data we collect">
            <P>The information we collect depends on how you interact with us. Broadly, we may collect:</P>
            <ul style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              <Li>Contact details (name, work email, phone number, role, organization)</Li>
              <Li>Information submitted via forms (e.g., reporting context, jurisdictions, frameworks, timelines)</Li>
              <Li>Account details for authorized users (login details, profile information)</Li>
              <Li>Communications with us (emails, meeting notes, support requests)</Li>
              <Li>Technical and usage data (log data, device information, usage patterns, cookies)</Li>
            </ul>
          </Section>

          <Section title="5. How we use personal data">
            <P>We use personal data to provide and operate the ESGine® platform, manage client accounts, respond to inquiries, configure and deliver pilots and briefings, improve our services, send service-related communications, and comply with legal and regulatory obligations.</P>
          </Section>

          <Section title="6. Legal bases for processing">
            <P>Where the UK GDPR or EU GDPR applies, we rely on legal bases including performance of a contract, legitimate interests, consent (where required), and compliance with legal obligations.</P>
            <P>In the United States, processing of personal data is governed by applicable federal and state privacy laws rather than a single unified legal basis framework. ESGine® processes personal data in accordance with applicable legal requirements, contractual obligations and reasonable business purposes.</P>
          </Section>

          <Section title="7. Sharing and international transfers">
            <P>We do not sell personal data. We may share information with service providers, professional advisers, regulators or authorities (where required), and in connection with corporate transactions, subject to appropriate safeguards.</P>
            <P>Where data is transferred across jurisdictions, including between the United Kingdom and the United States, we implement appropriate safeguards designed to maintain applicable data protection standards.</P>
          </Section>

          <Section title="8. Data retention">
            <P>We retain personal data for as long as necessary to fulfill the purposes described in this Privacy Policy, including to provide services, comply with legal obligations, resolve disputes and enforce agreements. Retention for materials uploaded to the ESGine® platform may be governed by our contract with the relevant client.</P>
          </Section>

          <Section title="9. Security">
            <P>We implement technical and organizational measures designed to protect personal data and client materials, including encryption in transit and at rest, access controls and logging. No system can be guaranteed as completely secure, and clients are responsible for maintaining the confidentiality of their account credentials.</P>
          </Section>

          <Section title="10. Your rights">
            <P>Depending on your location and subject to applicable law, you may have rights such as access, rectification, erasure, restriction, objection, data portability and the right to withdraw consent where processing is based on consent.</P>
            <P>Users in certain U.S. states, including California, may also have additional rights under applicable privacy laws such as the California Consumer Privacy Act (CCPA), including the right to access, delete or request information about personal data collected.</P>
            <P>You may also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) or another supervisory authority, where applicable.</P>
            <P>To exercise any of these rights, please contact us at <a href="mailto:info@esgine.io" style={{ color: '#1B6F5E' }}>info@esgine.io</a>.</P>
          </Section>

          <Section title="11. Changes to this Privacy Policy">
            <P>We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and, where appropriate, provide additional notice.</P>
          </Section>

        </div>
      </div>

      <Partners />
      <Compliance />
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, color: '#0B1F2D', letterSpacing: '-0.02em', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E8E5DC' }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function P({ children, style }) {
  return <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)', ...style }}>{children}</p>;
}

function Li({ children }) {
  return <li style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)' }}>{children}</li>;
}
